import { ByteLogo } from '../ByteLogo';
import { Icon } from './Icon';
import type { NavigationItem } from './types';
import type { PageKey } from './AppShell';

interface SidebarProps {
  navigationItems: NavigationItem[];
  currentPage: PageKey;
  onNavigate: (page: PageKey) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function Sidebar({
  navigationItems,
  currentPage,
  onNavigate,
  isCollapsed,
  onToggleCollapse,
}: SidebarProps) {
  return (
    <aside
      data-testid="nav-sidebar"
      className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex lg:flex-col bg-white border-r border-gray-200 transition-all duration-300 ${
        isCollapsed ? 'lg:w-20' : 'lg:w-64'
      }`}
    >
      {/* Desktop header */}
      <div className="flex items-center h-16 px-4 border-b border-gray-200">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <ByteLogo className="w-10 h-10" />
          </div>
          {!isCollapsed && (
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 truncate">Byte Quality</h1>
              <p className="text-xs text-gray-500">Quality Complaints</p>
            </div>
          )}
        </div>
      </div>

      {/* Desktop navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <ul className="space-y-1">
          {navigationItems.map((item) => (
            <li key={item.key}>
              {item.separator && <div className="my-3 border-t border-gray-200" />}
              <button
                onClick={() => onNavigate(item.key)}
                data-testid={`nav-link-${item.key}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors group relative ${
                  currentPage === item.key
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon
                  name={item.icon}
                  size={20}
                  className={`flex-shrink-0 ${
                    currentPage === item.key ? 'text-gray-900' : 'text-gray-500'
                  }`}
                />
                {!isCollapsed && (
                  <>
                    <span className="truncate">{item.label}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="ml-auto bg-primary-600 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
                {isCollapsed && item.badge !== undefined && item.badge > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-primary-600 rounded-full" />
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Desktop collapse toggle */}
      <div className="p-3 border-t border-gray-200">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon name={isCollapsed ? 'chevron-right' : 'chevron-left'} size={18} />
          {!isCollapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
